import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Car, Users, TrendingUp, ShoppingBag, AlertCircle, CheckCircle, ClipboardCheck, AlertTriangle, Clock } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, alertsRes] = await Promise.all([
        axios.get(`${API_URL}/api/dashboard/stats`, { headers }),
        axios.get(`${API_URL}/api/inspections/alerts`, { headers })
      ]);
      setStats(statsRes.data);
      setAlerts(alertsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ icon: Icon, label, value, color, link }) => (
    <Link 
      to={link}
      className="bg-[#12141D] border border-zinc-800 p-6 hover:border-zinc-600 transition-colors"
      data-testid={`stat-card-${label.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-zinc-500 text-sm mb-1">{label}</p>
          <p className={`text-3xl font-semibold ${color}`}>{value}</p>
        </div>
        <div className={`w-12 h-12 flex items-center justify-center bg-opacity-10 ${color.replace('text-', 'bg-')}`}>
          <Icon size={24} className={color} />
        </div>
      </div>
    </Link>
  );

  const getUrgencyStyles = (urgency) => {
    switch (urgency) {
      case 'overdue':
        return { border: 'border-red-600', bg: 'bg-red-600/10', text: 'text-red-400', icon: AlertCircle, label: 'VENCIDA' };
      case 'critical':
        return { border: 'border-orange-500', bg: 'bg-orange-500/10', text: 'text-orange-400', icon: AlertTriangle, label: 'URGENTE' };
      case 'warning':
        return { border: 'border-yellow-500', bg: 'bg-yellow-500/10', text: 'text-yellow-400', icon: AlertTriangle, label: 'ATENÇÃO' };
      default:
        return { border: 'border-blue-500', bg: 'bg-blue-500/10', text: 'text-blue-400', icon: Clock, label: 'EM BREVE' };
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const [y, m, d] = dateStr.split('-');
    return `${d}/${m}/${y}`;
  };

  return (
    <AdminLayout>
      <div className="animate-fade-in">
        <div className="mb-8">
          <h1 className="text-2xl font-medium text-white mb-2" data-testid="dashboard-title">Dashboard</h1>
          <p className="text-zinc-500">Visão geral do seu stand de automóveis</p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-zinc-400">A carregar estatísticas...</p>
          </div>
        ) : (
          <>
            {/* Inspection Alerts Banner */}
            {alerts.length > 0 && (
              <div className="mb-8 bg-[#12141D] border border-zinc-800 overflow-hidden" data-testid="inspection-alerts-section">
                <div className="flex items-center gap-3 px-6 py-4 border-b border-zinc-800 bg-red-600/5">
                  <ClipboardCheck size={22} className="text-red-500" />
                  <h3 className="text-lg font-medium text-white">Alertas de Inspeção</h3>
                  <span className="ml-auto bg-red-600 text-white text-xs font-bold px-2.5 py-1 rounded-full">
                    {alerts.length}
                  </span>
                </div>
                <div className="divide-y divide-zinc-800/50">
                  {alerts.map((alert) => {
                    const styles = getUrgencyStyles(alert.urgency);
                    const UrgencyIcon = styles.icon;
                    return (
                      <Link
                        key={alert.id}
                        to={`/admin/viaturas/${alert.id}`}
                        className={`flex items-center gap-4 px-6 py-4 hover:bg-white/[0.02] transition-colors`}
                        data-testid={`inspection-alert-${alert.id}`}
                      >
                        <div className={`w-10 h-10 flex items-center justify-center ${styles.bg} ${styles.border} border`}>
                          <UrgencyIcon size={20} className={styles.text} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium truncate">
                            {alert.brand} {alert.model} <span className="text-zinc-500">({alert.year})</span>
                          </p>
                          <p className="text-zinc-500 text-sm">
                            Próxima inspeção: {formatDate(alert.next_inspection_date)}
                          </p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <span className={`inline-block px-3 py-1 text-xs font-bold ${styles.bg} ${styles.text} ${styles.border} border`}>
                            {styles.label}
                          </span>
                          <p className={`text-sm font-semibold mt-1 ${styles.text}`}>
                            {alert.days_remaining < 0
                              ? `${Math.abs(alert.days_remaining)} dias em atraso`
                              : alert.days_remaining === 0
                                ? 'Hoje'
                                : `${alert.days_remaining} dias restantes`}
                          </p>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-testid="stats-grid">
              <StatCard 
                icon={Car} 
                label="Total Viaturas" 
                value={stats?.total_cars || 0} 
                color="text-white"
                link="/admin/viaturas"
              />
              <StatCard 
                icon={CheckCircle} 
                label="Disponíveis" 
                value={stats?.available_cars || 0} 
                color="text-emerald-500"
                link="/admin/viaturas"
              />
              <StatCard 
                icon={AlertCircle} 
                label="Reservadas" 
                value={stats?.reserved_cars || 0} 
                color="text-red-500"
                link="/admin/viaturas"
              />
              <StatCard 
                icon={ShoppingBag} 
                label="Vendidas" 
                value={stats?.sold_cars || 0} 
                color="text-red-500"
                link="/admin/viaturas"
              />
            </div>

            {/* Leads Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              <StatCard 
                icon={Users} 
                label="Total Contactos" 
                value={stats?.total_leads || 0} 
                color="text-blue-500"
                link="/admin/leads"
              />
              <StatCard 
                icon={TrendingUp} 
                label="Contactos Pendentes" 
                value={stats?.pending_leads || 0} 
                color="text-purple-500"
                link="/admin/leads"
              />
              <StatCard 
                icon={ClipboardCheck} 
                label="Alertas Inspeção" 
                value={stats?.inspection_alerts || 0} 
                color={stats?.inspection_alerts > 0 ? "text-orange-500" : "text-emerald-500"}
                link="/admin"
              />
            </div>

            {/* Quick Actions */}
            <div className="bg-[#12141D] border border-zinc-800 p-6">
              <h3 className="text-lg font-medium text-white mb-4">Ações Rápidas</h3>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/admin/viaturas/nova"
                  className="bg-red-600 text-white font-semibold px-6 py-3 hover:bg-red-700 transition-colors inline-flex items-center gap-2"
                  data-testid="quick-add-car-btn"
                >
                  <Car size={20} />
                  Adicionar Viatura
                </Link>
                <Link
                  to="/admin/leads"
                  className="border border-zinc-700 bg-transparent text-white px-6 py-3 hover:bg-zinc-800 transition-colors inline-flex items-center gap-2"
                  data-testid="quick-view-leads-btn"
                >
                  <Users size={20} />
                  Ver Contactos
                </Link>
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
